// Subscriptions — Base44 iOS design
// Two cards: Challenge Subscriptions + Market data subscriptions

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_ID, type Subscription } from '@funderpro/shared';
import { getToken, API_BASE } from '../../lib/auth';
import { Card, Empty, Loading, ErrorBox } from './UIShell';

export function SubscriptionsPage() {
  const [subs, setSubs] = useState<{ challenge: Subscription[]; market_data: Subscription[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = getToken();
    Promise.all([
      fetch(`${API_BASE}/api/apps/${APP_ID}/entities/Subscription?kind=challenge`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
      fetch(`${API_BASE}/api/apps/${APP_ID}/entities/Subscription?kind=market_data`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()),
    ]).then(([c, m]) => {
      setSubs({
        challenge: Array.isArray(c) ? c : [],
        market_data: Array.isArray(m) ? m : [],
      });
    }).catch(e => setErr(String(e?.message || e)));
  }, []);

  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (!subs) return <Loading />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-tf-text-1 md:text-3xl">Subscriptions</h1>

      <Card title="Challenge Subscriptions">
        {subs.challenge.length === 0 ? (
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#6b7aff]/15 text-[#6b7aff]">
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor"><path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm5 11V8h6l-2-2 1-1 4 4-4 4-1-1 2-2H10v6z"/></svg>
            </div>
            <p className="text-base font-semibold text-white">No subscriptions found</p>
            <p className="mt-1 text-sm text-[#5a6799]">Here you will find all your subscriptions</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {subs.challenge.map(s => (
              <li key={s.id} className="rounded-2xl border border-[#1a2348] bg-[#0a0e21]/40 p-3 text-sm">
                <p className="font-semibold text-white">{s.plan_name || s.market_data_name || 'Challenge'}</p>
                <p className="text-xs text-[#5a6799]">{s.status} · started {s.started_at ? new Date(s.started_at).toLocaleDateString() : '—'}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Market data subscriptions">
        {subs.market_data.length === 0 ? (
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#6b7aff]/15 text-[#6b7aff]">
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor"><path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm5 11V8h6l-2-2 1-1 4 4-4 4-1-1 2-2H10v6z"/></svg>
            </div>
            <p className="text-base font-semibold text-white">No market data subscription found</p>
            <p className="mt-1 text-sm text-[#5a6799]">Here you will find all your market data subscriptions</p>
            <Link to="/portal/platforms" className="mt-4 inline-block text-sm font-semibold text-[#8a9fff] hover:text-white">Browse platforms →</Link>
          </div>
        ) : (
          <ul className="space-y-2">
            {subs.market_data.map(s => (
              <li key={s.id} className="rounded-2xl border border-[#1a2348] bg-[#0a0e21]/40 p-3 text-sm">
                <p className="font-semibold text-white">{s.market_data_name || s.plan_name || 'Market data'}</p>
                <p className="text-xs text-[#5a6799]">{s.status}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
